import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@xxtract/ui';
import { ApiErrorMessage } from '@/components/feedback/ApiErrorMessage';
import { bouwReeks, korrelVoor, type Korrel, type Periode, type Tijdvak } from '../reeks';
import { isHandlingTimeGateClosed, type HandlingTimeBucket } from '../handlingTime.api';
import type { Normen } from '../normen';
import { stapVoorRangschikking, type StapFilter } from '../stapFilter';

/**
 * Story 31.3 — het verloop over de tijd: gestapelde staven met daaroverheen één lijn.
 *
 * De staven zeggen HOEVÉÉL er gewerkt is, de lijn zegt HOE EFFICIËNT. Die twee lopen niet gelijk
 * op — een drukke week met een dalende lijn is goed nieuws, een rustige week met een stijgende lijn
 * is een signaal — en los van elkaar getoond mist een lezer dat verband. Dat is de hele reden dat ze
 * in één beeld staan.
 *
 * > [!warning] Met de hand getekend, en dat is een eis
 * > Er zit geen grafiekbibliotheek in dit portaal en er komt er geen bij. Twee redenen, en de
 * > tweede weegt zwaarder. Eén: een bibliotheek is bundelgewicht dat niemand gemeten heeft. Twee:
 * > vrijwel elke grafiekbibliotheek vult een ontbrekend punt standaard met NUL. Dat gooit precies
 * > omver waar deze story om draait, en het gebeurt stilzwijgend — de grafiek ziet er dan uit alsof
 * > er hard maar vruchteloos gewerkt is.
 *
 * Wat hier met opzet NIET gebeurt:
 *
 * - Geen staaf voor een tijdvak zonder meting. Niet een staaf van hoogte nul, niet een onzichtbare
 *   staaf: geen staaf.
 * - Geen lijn dwars door zo'n tijdvak heen, en geen punt dat naar de bodem zakt.
 * - Geen stilzwijgend weglaten van zo'n tijdvak: dan leest de grafiek alsof die dagen niet bestonden.
 * - Geen gemiddelde over beide stappen samen. De lijn toont er één tegelijk.
 * - Geen groen, amber of rood. Die dragen sinds story 31.5 betekenis tegenover een norm, en een
 *   gegevensreeks die er een van leent zou als oordeel over elk punt erop lezen. Om diezelfde reden
 *   is de lijn van de reeks op 29 augustus 2026 van groen naar de rustige huisstijlkleur gegaan.
 * - Geen oordeel over de norm. De referentielijn zegt WAAR de norm ligt en verder niets: geen kleur
 *   die goed of fout betekent, geen waarschuwing, geen gevolg.
 */

export interface BehandeltijdGrafiekProps {
  periode: Periode;
  /**
   * De reeks zoals de bron hem gaf. AFWEZIG betekent "er is niet om een reeks gevraagd", een LEGE
   * lijst betekent "in dit venster is niets gemeten" — voor de tekening komt dat op hetzelfde neer:
   * er valt niets te tonen, en dat zegt het scherm dan ook.
   */
  buckets: HandlingTimeBucket[] | undefined;
  isLoading?: boolean;
  isError?: boolean;
  /** De fout zoals de vraag hem teruggaf; `isHandlingTimeGateClosed` haalt de dichte poort eruit. */
  error?: Error | null;
  onRetry?: () => void;
  /**
   * De naam van de persoon waarop deze reeks beperkt is, of `null` (story 31.4).
   *
   * Hij staat HIER en niet bij de tabel, want hier is te zien dát er gefilterd is: de tabel en de
   * kerncijfers blijven de hele periode tonen (AC-10), en zonder een melding bij de grafiek zou een
   * lezer een smallere grafiek naast bredere getallen zien zonder te weten waarom.
   */
  gefilterdOp?: string | null;
  /** Laat het filter los. Zonder deze knop is een filter een val: je ziet niet hoe je eruit komt. */
  onFilterLos?: () => void;
  /**
   * De twee normen (story 31.5). OPTIONEEL: zonder deze eigenschap tekent de grafiek geen
   * referentielijn, en dat is ook de stand zolang er geen norm gezet is.
   */
  normen?: Normen;
  /**
   * De stand van het stapfilter (story 33.1).
   *
   * De grafiek KIEST NIET MEER ZELF welke stap de lijn toont; hij volgt het filter bovenaan het
   * scherm. Dat was tot deze story een eigen schakelaar hier, en dat was precies één van de
   * bedieningen die naast het filter een tweede keuze zouden zijn (AC-11).
   *
   * Op `beide` toont de lijn vastleggen, dezelfde stap waarop de positiekolom dan rangschikt — één
   * lijn over twee ongelijksoortige stappen bestaat niet, dus er moet er één gekozen worden.
   *
   * De STAVEN volgen deze stand niet via deze eigenschap maar via de gegevens: het scherm levert
   * alleen de tijdvakken van de gekozen stap aan. Zo kan er geen staaf van een stap verschijnen die
   * het filter heeft weggelaten omdat de tekening het vergat.
   */
  stapFilter?: StapFilter;
}

const SLEUTEL = 'handlingTime.grafiek';

/** Welke stap de lijn toont. Altijd één, nooit allebei. */
type LijnStap = 'vastleggen' | 'valideren';


/* ── De tekening ───────────────────────────────────────────────────────────────────────────── */

const BREEDTE = 1000;
const HOOGTE = 340;
const MARGE_LINKS = 62;
const MARGE_RECHTS = 66;
const MARGE_BOVEN = 22;
/** Ruimte onder de as voor de datums én voor de tekst "niet gemeten" eronder. */
const MARGE_ONDER = 58;
const VLAK_BREED = BREEDTE - MARGE_LINKS - MARGE_RECHTS;
const VLAK_HOOG = HOOGTE - MARGE_BOVEN - MARGE_ONDER;
/** De y van de basislijn: waar de staven op staan en waar de lijn nooit hoort te komen. */
const BASISLIJN = MARGE_BOVEN + VLAK_HOOG;
const RASTERSTAPPEN = 4;

/** Een nette bovengrens: 1, 1,5, 2, 2,5, 5 of 10 maal een macht van tien. */
function nettePlafond(waarde: number): number {
  if (!(waarde > 0)) return 1;
  const macht = Math.pow(10, Math.floor(Math.log10(waarde)));
  const deel = waarde / macht;
  const net = deel <= 1 ? 1 : deel <= 1.5 ? 1.5 : deel <= 2 ? 2 : deel <= 2.5 ? 2.5 : deel <= 5 ? 5 : 10;
  return net * macht;
}

function alsDatum(datum: string): Date {
  const [jaar, maand, dag] = datum.split('-').map(Number);
  return new Date(jaar ?? 1970, (maand ?? 1) - 1, dag ?? 1);
}

export function BehandeltijdGrafiek({ periode, buckets, isLoading, isError, error, onRetry, gefilterdOp, onFilterLos, normen, stapFilter = 'beide' }: BehandeltijdGrafiekProps) {
  const { t, i18n } = useTranslation();
  const taal = i18n.language;
  const lijnStap: LijnStap = stapVoorRangschikking(stapFilter);

  const korrel: Korrel = korrelVoor(periode);
  // Op de DATUMS en niet op het object: het scherm maakt de periode bij elke weergave opnieuw aan,
  // en dan zou een geheugen op het object nooit iets onthouden.
  const tijdvakken = useMemo(
    () => bouwReeks({ from: periode.from, to: periode.to }, buckets, korrel),
    [periode.from, periode.to, buckets, korrel],
  );

  const kort = useMemo(() => new Intl.DateTimeFormat(taal, { day: 'numeric', month: 'short' }), [taal]);
  const maand = useMemo(() => new Intl.DateTimeFormat(taal, { month: 'short' }), [taal]);
  const lang = useMemo(() => new Intl.DateTimeFormat(taal, { day: 'numeric', month: 'long', year: 'numeric' }), [taal]);
  const getal = useMemo(() => new Intl.NumberFormat(taal, { maximumFractionDigits: 0 }), [taal]);
  const eenDecimaal = useMemo(() => new Intl.NumberFormat(taal, { minimumFractionDigits: 1, maximumFractionDigits: 1 }), [taal]);

  /** Het korte opschrift onder de as. */
  function asLabel(tijdvak: Tijdvak): string {
    const begin = alsDatum(tijdvak.van);
    const tekst = tijdvak.korrel === 'day'
      ? kort.format(begin)
      : tijdvak.korrel === 'week'
        ? t(`${SLEUTEL}.weekKort`, { datum: kort.format(begin) })
        : maand.format(begin);
    // Een randtijdvak draagt een sterretje; de voetnoot eronder legt uit waarom.
    return tijdvak.rand ? `${tekst}${t(`${SLEUTEL}.randMerk`)}` : tekst;
  }

  /** De volledige naam van het tijdvak, voor de zwevende toelichting en voorlezers. */
  function volleNaam(tijdvak: Tijdvak): string {
    const begin = alsDatum(tijdvak.van);
    if (tijdvak.korrel === 'day') return lang.format(begin);
    if (tijdvak.korrel === 'week') return t(`${SLEUTEL}.weekVan`, { datum: lang.format(begin) });
    return new Intl.DateTimeFormat(taal, { month: 'long', year: 'numeric' }).format(begin);
  }

  /**
   * De melding dat deze grafiek op één persoon staat, met de knop die hem loslaat.
   *
   * Hij staat in ELKE tak hieronder, ook in die van een storing of een lege reeks: juist dan is het
   * de vraag of er niets gemeten is of dat het filter te smal staat.
   */
  const filter = gefilterdOp == null || gefilterdOp === '' ? null : (
    <div
      className="mt-3 flex flex-wrap items-center gap-2 rounded-[var(--radius-badge)] border border-[var(--primary)] bg-[var(--primary-faint)] px-3 py-1.5 text-xs"
      data-testid="handling-time-filter-chip"
    >
      <span aria-hidden="true" className="mdi mdi-account-filter text-sm text-[var(--primary)]" />
      <span className="font-semibold text-[var(--foreground-heading)]">
        {t(`${SLEUTEL}.filterTitel`, { naam: gefilterdOp })}
      </span>
      <span className="text-[var(--foreground-muted)]">{t(`${SLEUTEL}.filterUitleg`)}</span>
      <button
        className="ml-auto rounded-[var(--radius-control)] bg-[var(--primary)] px-2 py-0.5 text-xs font-semibold text-white"
        data-testid="handling-time-filter-los"
        onClick={onFilterLos}
        type="button"
      >
        {t(`${SLEUTEL}.filterLos`)}
      </button>
    </div>
  );

  // ── De randen, vóór er iets getekend wordt ──

  if (isError && isHandlingTimeGateClosed(error)) {
    // Dicht is geen storing, en zeker geen rode grafiek. Het scherm zegt dit al bovenaan; hier
    // staat alleen dat er daarom niets te tekenen valt.
    return (
      <Card className="p-5" data-testid="handling-time-grafiek">
        <p className="text-sm text-[var(--foreground-muted)]" data-testid="handling-time-grafiek-poort-dicht">
          {t(`${SLEUTEL}.poortDicht`)}
        </p>
        {filter}
      </Card>
    );
  }

  if (isError) {
    // De grafiek meldt het ZELF en trekt het scherm niet mee: de kerncijfers en de tabel eromheen
    // staan er gewoon, want die komen uit een andere vraag.
    return (
      <Card className="p-5" data-testid="handling-time-grafiek">
        <h2 className="text-sm font-semibold text-[var(--foreground)]">{t(`${SLEUTEL}.titel`)}</h2>
        {filter}
        <div className="mt-3" data-testid="handling-time-grafiek-fout">
          <ApiErrorMessage error={error ?? null} onRetry={onRetry} />
        </div>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card className="p-5" data-testid="handling-time-grafiek">
        <h2 className="text-sm font-semibold text-[var(--foreground)]">{t(`${SLEUTEL}.titel`)}</h2>
        {filter}
        <div className="skeleton mt-3 h-64 rounded-lg" data-testid="handling-time-grafiek-laden" />
      </Card>
    );
  }

  const gemeten = tijdvakken.filter((tijdvak) => tijdvak.gemeten);
  const leeg = tijdvakken.length - gemeten.length;
  const heeftRand = tijdvakken.some((tijdvak) => tijdvak.rand);

  const korrelNaam = t(`${SLEUTEL}.korrel.${korrel}`);

  if (gemeten.length === 0) {
    // Geen lege assenkruising: een raster zonder één staaf leest als "het is nul geweest", en dat
    // is precies wat er niet gemeten is.
    return (
      <Card className="p-5" data-testid="handling-time-grafiek">
        <h2 className="text-sm font-semibold text-[var(--foreground)]">{t(`${SLEUTEL}.titel`)}</h2>
        {filter}
        <p className="mt-2 text-sm text-[var(--foreground-muted)]" data-testid="handling-time-grafiek-leeg">
          {t(`${SLEUTEL}.niets`)}
        </p>
      </Card>
    );
  }

  // ── De schalen ──

  const maxMinuten = Math.max(...gemeten.map((tijdvak) => tijdvak.totaleMinuten ?? 0));
  // Geen vangnet erachter: `nettePlafond` geeft bij elke invoer minstens 1 terug, en de tak
  // hierboven heeft al vastgesteld dat er iets gemeten is. Een vangnet dat nooit afgaat leest als
  // een bescherming die er niet is (reviewbevinding 27-08-2026).
  const maxLinks = nettePlafond(maxMinuten * 1.1);

  const gemiddelden = tijdvakken
    .map((tijdvak) => tijdvak[lijnStap].gemiddeldePerStuk)
    .filter((waarde): waarde is number => waarde !== null);
  /**
   * De norm van de GETOONDE stap, of niets. Een norm van nul is geen norm; die komt de serverlaag
   * niet door en wordt hier ook niet als norm behandeld.
   */
  const gekozenNorm = normen?.[lijnStap] ?? null;
  const norm = gekozenNorm !== null && gekozenNorm > 0 ? gekozenNorm : null;
  // De NORM TELT MEE in de bovengrens van de rechteras, en dat is geen verfraaiing: een norm die
  // boven alles uitkomt wat er gemeten is — precies het geval waarin je hem het hardst wil zien —
  // zou anders buiten het tekenvlak vallen. Deze stap houdt hem er altijd binnen.
  const maxRechts = nettePlafond(Math.max(...gemiddelden, norm ?? 0, 0) * 1.25);

  const aantal = tijdvakken.length;
  const vak = VLAK_BREED / aantal;
  const staafBreed = Math.max(3, Math.min(26, vak * 0.6));
  const elkeZoveelste = Math.ceil(aantal / 16);

  /** De x van het midden van een tijdvak. */
  function midden(index: number): number {
    return MARGE_LINKS + vak * (index + 0.5);
  }

  /** De punten van de lijn; `null` waar er geen meting is — daar hoort een GAT. */
  const punten: Array<{ x: number; y: number; tijdvak: Tijdvak } | null> = tijdvakken.map((tijdvak, index) => {
    const waarde = tijdvak.gemeten ? tijdvak[lijnStap].gemiddeldePerStuk : null;
    if (waarde === null) return null;
    return { x: midden(index), y: BASISLIJN - (waarde / maxRechts) * VLAK_HOOG, tijdvak };
  });

  /**
   * De lijn in losse stukken.
   *
   * Elk aaneengesloten stuk krijgt zijn eigen pad; de lijn springt nooit over een leeg tijdvak
   * heen. Een meting die er helemaal alleen voor staat wordt een punt — een pad van één punt tekent
   * niets, en dan zou die meting onzichtbaar zijn.
   */
  const stukken: Array<Array<{ x: number; y: number; tijdvak: Tijdvak }>> = [];
  let lopend: Array<{ x: number; y: number; tijdvak: Tijdvak }> = [];
  for (const punt of [...punten, null]) {
    if (punt !== null) { lopend.push(punt); continue; }
    if (lopend.length > 0) stukken.push(lopend);
    lopend = [];
  }

  /**
   * De paden van de lijn, hier opgebouwd en niet in de tekening zelf.
   *
   * Elk stuk van meer dan één punt wordt een pad; een stuk van één punt krijgt alleen zijn
   * merkteken, want een pad van één punt tekent niets.
   */
  const paden = stukken
    .filter((stuk) => stuk.length > 1)
    .map((stuk) => ({
      sleutel: stuk[0]!.tijdvak.label,
      pad: `M${stuk.map((punt) => `${punt.x.toFixed(1)} ${punt.y.toFixed(1)}`).join(' L')}`,
    }));

  /** Aaneengesloten reeksen lege tijdvakken, zodat "niet gemeten" één keer per gat staat. */
  const gaten: Array<{ van: number; tot: number }> = [];
  for (let index = 0; index < aantal; index += 1) {
    if (tijdvakken[index]!.gemeten) continue;
    const laatste = gaten[gaten.length - 1];
    if (laatste !== undefined && laatste.tot === index - 1) laatste.tot = index;
    else gaten.push({ van: index, tot: index });
  }

  // "1 tijdvakken" las als een fout in het scherm en niet als een fout in de taal
  // (reviewbevinding 27-08-2026). De telling gaat nu door de meervoudsregel van de vertaling, die
  // per taal anders kan liggen — dat is precies waarom hij daar hoort en niet hier.
  // OOK DE VOORGELEZEN BESCHRIJVING volgt het filter, en dat is een codereviewbevinding van
  // 30 augustus 2026: de zichtbare ondertitel kreeg wél zijn eigen zin op één stap, deze niet. Een
  // schermlezer hoorde daardoor "vastleggen onder, valideren erboven" over een tekening waarin de
  // andere stap juist met opzet ontbreekt (AC-3 en AC-17).
  const beschrijving = t(`${SLEUTEL}.${stapFilter === 'beide' ? 'beschrijving' : 'beschrijvingStap'}`, {
    korrel: korrelNaam,
    stap: t(`${SLEUTEL}.stap.${lijnStap}`).toLowerCase(),
    // Het meervoud staat ÉÉN keer, op het totaal: "4 tijdvakken met een meting, 3 tijdvakken
    // zonder meting" is grammaticaal goed en leest houterig. De twee getallen erna zijn kaal.
    totaal: t(`${SLEUTEL}.tijdvak`, { count: tijdvakken.length }),
    gemeten: gemeten.length,
    leeg,
  });

  return (
    <Card className="p-5" data-testid="handling-time-grafiek">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-[var(--foreground)]" id="handling-time-grafiek-titel">
            {t(`${SLEUTEL}.titel`)}
          </h2>
          {/* De ondertitel beschrijft de STAPELING van de twee stappen; op één stap is er niets
              gestapeld en zou hij een tweede kleur aankondigen die er niet staat. */}
          <p className="mt-1 text-xs text-[var(--foreground-muted)]">
            {stapFilter === 'beide'
              ? t(`${SLEUTEL}.ondertitel`)
              : t(`${SLEUTEL}.ondertitelStap`, { stap: t(`${SLEUTEL}.stap.${stapFilter}`).toLowerCase() })}
          </p>
        </div>
      </div>

      {filter}

      <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-[var(--foreground-muted)]">
        <span
          className="rounded-[var(--radius-badge)] bg-[var(--background-muted)] px-2 py-0.5 font-medium"
          data-testid="handling-time-korrel"
        >
          {t(`${SLEUTEL}.korrelBadge`, { korrel: korrelNaam })}
        </span>
        <span data-testid="handling-time-korrel-uitleg">{t(`${SLEUTEL}.korrelUitleg`)}</span>
      </div>

      <div className="mt-3 overflow-x-auto">
        <svg
          aria-describedby="handling-time-grafiek-beschrijving"
          aria-labelledby="handling-time-grafiek-titel"
          className="block h-auto w-full min-w-[38rem]"
          data-testid="handling-time-grafiek-svg"
          role="img"
          viewBox={`0 0 ${BREEDTE} ${HOOGTE}`}
        >
          {/* Raster en de twee assen. Links de bestede tijd in uren, rechts de minuten per stuk. */}
          {Array.from({ length: RASTERSTAPPEN + 1 }).map((_, stap) => {
            const y = BASISLIJN - (VLAK_HOOG * stap) / RASTERSTAPPEN;
            return (
              <g key={`raster-${stap}`}>
                <line stroke="var(--serie-raster)" strokeWidth={1} x1={MARGE_LINKS} x2={MARGE_LINKS + VLAK_BREED} y1={y} y2={y} />
                <text className="text-[11px]" fill="var(--foreground-muted)" textAnchor="end" x={MARGE_LINKS - 10} y={y + 4}>
                  {t(`${SLEUTEL}.uren`, { waarde: getal.format((maxLinks * stap) / RASTERSTAPPEN / 60) })}
                </text>
                <text className="text-[11px]" fill="var(--foreground-muted)" textAnchor="start" x={MARGE_LINKS + VLAK_BREED + 10} y={y + 4}>
                  {eenDecimaal.format((maxRechts * stap) / RASTERSTAPPEN)}
                </text>
              </g>
            );
          })}
          <text className="text-[11px] font-semibold" fill="var(--foreground-muted)" textAnchor="start" x={MARGE_LINKS} y={MARGE_BOVEN - 6}>
            {t(`${SLEUTEL}.asLinks`)}
          </text>
          <text className="text-[11px] font-semibold" fill="var(--foreground-muted)" textAnchor="end" x={MARGE_LINKS + VLAK_BREED} y={MARGE_BOVEN - 6}>
            {t(`${SLEUTEL}.asRechts`)}
          </text>

          {/* De tijdvakken: een staaf waar gemeten is, en een merkteken waar dat niet zo is. */}
          {tijdvakken.map((tijdvak, index) => {
            const x = midden(index);
            const hoogteVastleggen = ((tijdvak.vastleggen.minuten ?? 0) / maxLinks) * VLAK_HOOG;
            const hoogteValideren = ((tijdvak.valideren.minuten ?? 0) / maxLinks) * VLAK_HOOG;
            return (
              <g data-gemeten={String(tijdvak.gemeten)} data-rand={String(tijdvak.rand)} data-testid={`tijdvak-${tijdvak.label}`} key={tijdvak.label}>
                <title>
                  {tijdvak.gemeten
                    ? t(`${SLEUTEL}.toelichting`, {
                      naam: volleNaam(tijdvak),
                      totaal: getal.format(tijdvak.totaleMinuten ?? 0),
                    })
                    : t(`${SLEUTEL}.toelichtingLeeg`, { naam: volleNaam(tijdvak) })}
                </title>
                {/* TWEE POORTEN, en geen van beide is dubbelop — nagemeten met mutaties op
                    27 augustus 2026. Deze buitenste houdt een tijdvak tegen waarin NIETS gemeten is:
                    haal hem weg en de "niet gemeten"-aanduiding verdwijnt (AC-7 valt om). De poort
                    per stap hieronder houdt een stap tegen binnen een tijdvak waarin de ándere stap
                    wél gemeten is: haal díe weg en er verschijnt een blok van nul (AC-8 valt om).
                    Dat AC-5 het wegvallen van één van de twee overleeft is precies de bedoeling. */}
                {tijdvak.gemeten ? (
                  <>
                    {/* Vastleggen ONDER, op de basislijn. Alleen als er werkelijk minuten zijn:
                        een stap zonder meting krijgt geen blok, ook geen blok van nul. */}
                    {tijdvak.vastleggen.minuten !== null && tijdvak.vastleggen.minuten > 0 && (
                      <rect
                        data-testid={`staaf-vastleggen-${tijdvak.label}`}
                        fill="var(--serie-vastleggen)"
                        height={hoogteVastleggen}
                        rx={1.5}
                        width={staafBreed}
                        x={x - staafBreed / 2}
                        y={BASISLIJN - hoogteVastleggen}
                      />
                    )}
                    {/* Valideren ERBOVEN, zodat de totale hoogte de totale tijd is. */}
                    {tijdvak.valideren.minuten !== null && tijdvak.valideren.minuten > 0 && (
                      <rect
                        data-testid={`staaf-valideren-${tijdvak.label}`}
                        fill="var(--serie-valideren)"
                        height={hoogteValideren}
                        rx={1.5}
                        width={staafBreed}
                        x={x - staafBreed / 2}
                        y={BASISLIJN - hoogteVastleggen - hoogteValideren}
                      />
                    )}
                  </>
                ) : (
                  // GEEN staaf. Wel een merkteken, want stil weglaten leest alsof deze dagen niet
                  // bestonden — en dat is iets anders dan een dag waarop niemand iets deed.
                  <g data-testid={`tijdvak-leeg-${tijdvak.label}`}>
                    <rect
                      fill="var(--serie-leeg)"
                      fillOpacity={0.08}
                      height={VLAK_HOOG}
                      width={vak}
                      x={x - vak / 2}
                      y={MARGE_BOVEN}
                    />
                    <line
                      stroke="var(--serie-leeg)"
                      strokeDasharray="3 3"
                      strokeWidth={2}
                      x1={x - staafBreed / 2}
                      x2={x + staafBreed / 2}
                      y1={BASISLIJN}
                      y2={BASISLIJN}
                    />
                  </g>
                )}
                {index % elkeZoveelste === 0 && (
                  <text className="text-[11px]" fill="var(--foreground-muted)" textAnchor="middle" x={x} y={BASISLIJN + 18}>
                    {asLabel(tijdvak)}
                  </text>
                )}
              </g>
            );
          })}

          {/* Eén keer "niet gemeten" per aaneengesloten gat, zodat twee weekenddagen niet over
              elkaar heen schrijven. */}
          {gaten.map((gat) => (
            <text
              className="text-[10px]"
              data-testid={`niet-gemeten-${tijdvakken[gat.van]!.label}`}
              fill="var(--serie-leeg)"
              key={`gat-${gat.van}`}
              textAnchor="middle"
              x={MARGE_LINKS + vak * ((gat.van + gat.tot + 1) / 2)}
              y={BASISLIJN + 34}
            >
              {t(`${SLEUTEL}.nietGemeten`)}
            </text>
          ))}

          <line
            data-testid="grafiek-basislijn"
            stroke="var(--border)"
            strokeWidth={1}
            x1={MARGE_LINKS}
            x2={MARGE_LINKS + VLAK_BREED}
            y1={BASISLIJN}
            y2={BASISLIJN}
          />

          {/* DE REFERENTIELIJN OP DE NORM van de getoonde stap (story 31.5).
              GESTIPPELD, zodat hij niet voor een meting wordt aangezien, en in de gedempte
              tekstkleur: hij zegt WAAR de norm ligt en velt geen oordeel — groen, amber en rood
              zijn voor de tabel, niet voor deze lijn.
              Hij hangt NIET aan de data: een tijdvak zonder meting haalt hem niet weg, want de norm
              gold ook in die week. */}
          {norm !== null && (
            <line
              data-testid="grafiek-normlijn"
              stroke="var(--foreground-muted)"
              strokeDasharray="6 4"
              strokeWidth={1.5}
              x1={MARGE_LINKS}
              x2={MARGE_LINKS + VLAK_BREED}
              y1={BASISLIJN - (norm / maxRechts) * VLAK_HOOG}
              y2={BASISLIJN - (norm / maxRechts) * VLAK_HOOG}
            />
          )}

          {/* De lijn, in losse stukken. Nooit dwars door een leeg tijdvak heen. */}
          {paden.map((stuk) => (
            <path
              d={stuk.pad}
              data-testid="lijn-segment"
              fill="none"
              key={stuk.sleutel}
              stroke="var(--serie-lijn)"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.25}
            />
          ))}
          {/* Merktekens op elk punt. Hier stond een drempel voor "te veel tijdvakken", maar die
              kon niet afgaan: de bron weigert periodes boven 366 dagen, en dat is hoogstens 31
              dagvakken, 27 weken of 13 maanden. Code die nooit draait is geen vangnet
              (reviewbevinding 27-08-2026). Een pad van één punt tekent niets, dus juist een
              alleenstaande meting heeft zijn merkteken nodig — en die krijgt hij nu altijd. */}
          {stukken.flat().map((punt) => (
            <circle
              cx={punt.x}
              cy={punt.y}
              data-testid={`lijn-punt-${punt.tijdvak.label}`}
              fill="var(--serie-lijn)"
              key={`punt-${punt.tijdvak.label}`}
              r={2.8}
            />
          ))}
        </svg>
      </div>

      {/* De legenda, en daaronder wat een voorlezer te horen krijgt. */}
      <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-[var(--foreground-muted)]">
        {/* Alleen de stap of stappen die er werkelijk staan: een legenda voor een kleur die
            nergens in de tekening voorkomt laat een lezer zoeken naar staven die er niet zijn. */}
        {stapFilter !== 'valideren' && (
          <span className="flex items-center gap-1.5">
            <span aria-hidden="true" className="size-3 rounded-[3px] bg-[var(--serie-vastleggen)]" />
            {t(`${SLEUTEL}.stap.vastleggen`)}
          </span>
        )}
        {stapFilter !== 'vastleggen' && (
          <span className="flex items-center gap-1.5">
            <span aria-hidden="true" className="size-3 rounded-[3px] bg-[var(--serie-valideren)]" />
            {t(`${SLEUTEL}.stap.valideren`)}
          </span>
        )}
        <span className="flex items-center gap-1.5" data-testid="grafiek-lijn-legenda">
          <span aria-hidden="true" className="h-0 w-5 border-t-2 border-[var(--serie-lijn)]" />
          {t(`${SLEUTEL}.legendaLijn`, { stap: t(`${SLEUTEL}.stap.${lijnStap}`).toLowerCase() })}
        </span>
        <span className="flex items-center gap-1.5">
          <span aria-hidden="true" className="h-0 w-5 border-t-2 border-dashed border-[var(--serie-leeg)]" />
          {t(`${SLEUTEL}.nietGemeten`)}
        </span>
        {/* Een streepjeslijn zonder naam is een raadsel: wie hem niet kan thuisbrengen leest hem als
            een tweede meting. Hij staat er alleen wanneer de lijn er ook staat. */}
        {norm !== null && (
          <span className="flex items-center gap-1.5" data-testid="grafiek-normlijn-legenda">
            <span aria-hidden="true" className="h-0 w-5 border-t-2 border-dashed border-[var(--foreground-muted)]" />
            {t(`${SLEUTEL}.normLijn`, {
              stap: t(`${SLEUTEL}.stap.${lijnStap}`).toLowerCase(),
              waarde: eenDecimaal.format(norm),
            })}
          </span>
        )}
      </div>

      {heeftRand && (
        <p className="mt-2 text-[11px] leading-snug text-[var(--foreground-muted)]" data-testid="handling-time-randtijdvak">
          {t(`${SLEUTEL}.randUitleg`, { merk: t(`${SLEUTEL}.randMerk`) })}
        </p>
      )}

      <p className="sr-only" data-testid="handling-time-grafiek-beschrijving" id="handling-time-grafiek-beschrijving">
        {beschrijving}
      </p>
    </Card>
  );
}
